import React, { useEffect, useState } from 'react';
import { ShoppingBagIcon, ChevronLeftIcon, PlusIcon, TagIcon, PackageIcon, ImageIcon, SearchIcon, FilterIcon, LayoutGridIcon, ListIcon, QrCodeIcon, LinkIcon, CopyIcon, TrashIcon, EditIcon, CheckIcon, XIcon, ArchiveIcon, EyeIcon, DollarSignIcon, BarChartIcon } from 'lucide-react';
import { Shop } from './Shops';
import { ShopDashboard } from '../components/ShopDashboard';
// Product type definition
interface Product {
  id: string;
  name: string;
  description: string;
  price: number;
  discountedPrice?: number;
  categoryId: string;
  imageUrl: string;
  stockQuantity: number;
  sku: string;
  isActive: boolean;
}
// Category type definition
interface Category {
  id: string;
  name: string;
  description: string;
  productCount: number;
}
export function ShopDetails() {
  // Get shop ID from URL parameter
  const getShopId = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('id');
    }
    return null;
  };
  const isNewShop = () => {
    if (typeof window !== 'undefined') {
      const urlParams = new URLSearchParams(window.location.search);
      return urlParams.get('new') === 'true';
    }
    return false;
  };
  // Shop state
  const [shop, setShop] = useState<Shop | null>(null);
  const [isEditing, setIsEditing] = useState(isNewShop());
  const [activeTab, setActiveTab] = useState<'dashboard' | 'products' | 'categories' | 'settings'>('dashboard');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | 'all'>('all');
  // Products and categories state
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  // Form state for new shop
  const [shopForm, setShopForm] = useState({
    name: '',
    description: '',
    logo: '•',
    color: '#f8d7d7',
    channels: ['ecommerce']
  });
  // Mock data loading
  useEffect(() => {
    const shopId = getShopId();
    if (shopId && !isNewShop()) {
      // Mock shop data
      const mockShop: Shop = {
        id: shopId,
        name: 'Beach Shop',
        description: 'Beachwear and accessories for summer holidays',
        logo: '•',
        color: '#f8d7d7',
        isOpen: true,
        channels: ['pos', 'ecommerce'],
        productsCount: 48,
        revenue: {
          total: 12580,
          currency: 'USD',
          growth: 15.3
        },
        ordersCount: 124
      };
      setShop(mockShop);
      setShopForm({
        name: mockShop.name,
        description: mockShop.description,
        logo: mockShop.logo,
        color: mockShop.color,
        channels: [...mockShop.channels]
      });
      // Mock categories
      setCategories([{
        id: 'cat1',
        name: 'Swimwear',
        description: 'Swimsuits and beachwear',
        productCount: 12
      }, {
        id: 'cat2',
        name: 'Accessories',
        description: 'Beach accessories',
        productCount: 18
      }, {
        id: 'cat3',
        name: 'Footwear',
        description: 'Sandals and beach shoes',
        productCount: 8
      }, {
        id: 'cat4',
        name: 'Souvenirs',
        description: 'Memorable items from the beach',
        productCount: 10
      }]);
      // Mock products
      setProducts([{
        id: 'prod1',
        name: 'Beach Towel',
        description: 'Large absorbent beach towel',
        price: 29.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1601924582970-9238bcb495d9?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 45,
        sku: 'BT-001',
        isActive: true
      }, {
        id: 'prod2',
        name: 'Sunglasses',
        description: 'UV protection sunglasses',
        price: 49.99,
        discountedPrice: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1577803645773-f96470509666?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 32,
        sku: 'SG-002',
        isActive: true
      }, {
        id: 'prod3',
        name: 'Swim Shorts',
        description: 'Quick-dry swim shorts for men',
        price: 34.99,
        categoryId: 'cat1',
        imageUrl: 'https://images.unsplash.com/photo-1565128939522-7a321311972a?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 28,
        sku: 'SS-003',
        isActive: true
      }, {
        id: 'prod4',
        name: 'Beach Sandals',
        description: 'Comfortable sandals for the beach',
        price: 24.99,
        categoryId: 'cat3',
        imageUrl: 'https://images.unsplash.com/photo-1603487742131-4160ec999306?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 50,
        sku: 'BS-004',
        isActive: true
      }, {
        id: 'prod5',
        name: 'Beach Ball',
        description: 'Colorful inflatable beach ball',
        price: 12.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1610027240617-46262bfb32a8?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 65,
        sku: 'BB-005',
        isActive: true
      }, {
        id: 'prod6',
        name: 'Beach Umbrella',
        description: 'Sun protection beach umbrella',
        price: 39.99,
        categoryId: 'cat2',
        imageUrl: 'https://images.unsplash.com/photo-1519046308686-24180d0f8087?ixlib=rb-1.2.1&auto=format&fit=crop&w=300&q=80',
        stockQuantity: 20,
        sku: 'BU-006',
        isActive: true
      }]);
    }
  }, []);
  // Filter products based on search and category
  const filteredProducts = products.filter(product => {
    const matchesSearch = product.name.toLowerCase().includes(searchTerm.toLowerCase()) || product.description.toLowerCase().includes(searchTerm.toLowerCase()) || product.sku.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesCategory = selectedCategoryId === 'all' || product.categoryId === selectedCategoryId;
    return matchesSearch && matchesCategory;
  });
  // Save shop
  const saveShop = () => {
    if (isNewShop()) {
      // Logic to create new shop
      window.location.href = '/shops';
    } else {
      // Logic to update shop
      setIsEditing(false);
    }
  };
  // Navigate back to shops
  const navigateToShops = () => {
    window.location.href = '/shops';
  };
  // Toggle product active status
  const toggleProductStatus = (productId: string) => {
    setProducts(products.map(product => product.id === productId ? {
      ...product,
      isActive: !product.isActive
    } : product));
  };
  // Render shop form
  const renderShopForm = () => <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3799">
      <h2 className="text-lg font-medium text-gray-900 mb-4" data-id="element-3800">
        {isNewShop() ? 'Create New Shop' : 'Edit Shop'}
      </h2>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-3801">
        <div data-id="element-3802">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3803">
            Shop Name *
          </label>
          <input type="text" value={shopForm.name} onChange={e => setShopForm({
          ...shopForm,
          name: e.target.value
        })} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Enter shop name" required data-id="element-3804" />
        </div>
        <div data-id="element-3805">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3806">
            Shop Logo
          </label>
          <input type="text" value={shopForm.logo} onChange={e => setShopForm({
          ...shopForm,
          logo: e.target.value
        })} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Logo character or emoji" maxLength={2} data-id="element-3807" />
          <p className="mt-1 text-xs text-gray-500" data-id="element-3808">
            Enter a single character or emoji to use as shop logo
          </p>
        </div>
        <div className="col-span-2" data-id="element-3809">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-3810">
            Description
          </label>
          <textarea value={shopForm.description} onChange={e => setShopForm({
          ...shopForm,
          description: e.target.value
        })} rows={3} className="block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Describe your shop" data-id="element-3811" />
        </div>
        <div data-id="element-3812">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3813">
            Logo Background Color
          </label>
          <div className="flex items-center" data-id="element-3814">
            <input type="color" value={shopForm.color} onChange={e => setShopForm({
            ...shopForm,
            color: e.target.value
          })} className="w-10 h-10 border-0 p-0" data-id="element-3815" />
            <input type="text" value={shopForm.color} onChange={e => setShopForm({
            ...shopForm,
            color: e.target.value
          })} className="block ml-2 w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" data-id="element-3816" />
          </div>
        </div>
        <div data-id="element-3817">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-3818">
            Shop Channels
          </label>
          <div className="space-y-2" data-id="element-3819">
            <label className="flex items-center" data-id="element-3820">
              <input type="checkbox" checked={shopForm.channels.includes('ecommerce')} onChange={e => {
              const newChannels = e.target.checked ? [...shopForm.channels, 'ecommerce'] : shopForm.channels.filter(c => c !== 'ecommerce');
              setShopForm({
                ...shopForm,
                channels: newChannels as ('pos' | 'ecommerce')[]
              });
            }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-3821" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-3822">
                E-Commerce (Online Shop)
              </span>
            </label>
            <label className="flex items-center" data-id="element-3823">
              <input type="checkbox" checked={shopForm.channels.includes('pos')} onChange={e => {
              const newChannels = e.target.checked ? [...shopForm.channels, 'pos'] : shopForm.channels.filter(c => c !== 'pos');
              setShopForm({
                ...shopForm,
                channels: newChannels as ('pos' | 'ecommerce')[]
              });
            }} className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded" data-id="element-3824" />
              <span className="ml-2 text-sm text-gray-700" data-id="element-3825">
                Point of Sale (Physical Store)
              </span>
            </label>
          </div>
        </div>
      </div>
      <div className="mt-6 flex justify-end space-x-3" data-id="element-3826">
        <button onClick={navigateToShops} className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50" data-id="element-3827">
          Cancel
        </button>
        <button onClick={saveShop} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium" data-id="element-3828">
          {isNewShop() ? 'Create Shop' : 'Save Changes'}
        </button>
      </div>
    </div>;
  // Render shop header
  const renderShopHeader = () => <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6" data-id="element-3829">
      <div className="flex items-start justify-between" data-id="element-3830">
        <div className="flex items-start space-x-4" data-id="element-3831">
          <div className="w-16 h-16 rounded-md flex items-center justify-center flex-shrink-0" style={{
          backgroundColor: shop?.color
        }} data-id="element-3832">
            <span className="text-2xl font-bold text-red-600" data-id="element-3833">
              {shop?.logo}
            </span>
          </div>
          <div data-id="element-3834">
            <h2 className="text-2xl font-bold text-gray-900" data-id="element-3835">{shop?.name}</h2>
            <p className="text-gray-500 mt-1" data-id="element-3836">{shop?.description}</p>
            <div className="flex items-center mt-2 space-x-3" data-id="element-3837">
              {/* Shop channels */}
              {shop?.channels.includes('pos') && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-purple-100 text-purple-800" data-id="element-3838">
                  POS
                </span>}
              {shop?.channels.includes('ecommerce') && <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3839">
                  E-Commerce
                </span>}
              {/* Shop status */}
              <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${shop?.isOpen ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-3840">
                {shop?.isOpen ? 'Open' : 'Closed'}
              </span>
            </div>
          </div>
        </div>
        {/* Shop actions */}
        <div className="flex items-center space-x-2" data-id="element-3841">
          <button onClick={() => setIsEditing(true)} className="p-2 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md" title="Edit Shop" data-id="element-3842">
            <EditIcon size={18} data-id="element-3843" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="QR Code" data-id="element-3844">
            <QrCodeIcon size={18} data-id="element-3845" />
          </button>
          <button className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded-md" title="Shop Link" data-id="element-3846">
            <LinkIcon size={18} data-id="element-3847" />
          </button>
        </div>
      </div>
      {/* Shop statistics */}
      <div className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-gray-200 pt-4" data-id="element-3848">
        <div className="flex items-center" data-id="element-3849">
          <div className="p-2 bg-blue-50 rounded-md mr-3" data-id="element-3850">
            <PackageIcon size={18} className="text-blue-600" data-id="element-3851" />
          </div>
          <div data-id="element-3852">
            <p className="text-sm text-gray-500" data-id="element-3853">Products</p>
            <p className="text-lg font-medium" data-id="element-3854">{shop?.productsCount}</p>
          </div>
        </div>
        <div className="flex items-center" data-id="element-3855">
          <div className="p-2 bg-green-50 rounded-md mr-3" data-id="element-3856">
            <DollarSignIcon size={18} className="text-green-600" data-id="element-3857" />
          </div>
          <div data-id="element-3858">
            <p className="text-sm text-gray-500" data-id="element-3859">Revenue</p>
            <div className="flex items-center" data-id="element-3860">
              <p className="text-lg font-medium" data-id="element-3861">
                ${shop?.revenue.total.toLocaleString()}
              </p>
              <span className={`ml-2 text-xs ${shop?.revenue.growth && shop.revenue.growth >= 0 ? 'text-green-600' : 'text-red-600'}`} data-id="element-3862">
                {shop?.revenue.growth && shop.revenue.growth >= 0 ? '+' : ''}
                {shop?.revenue.growth}%
              </span>
            </div>
          </div>
        </div>
        <div className="flex items-center" data-id="element-3863">
          <div className="p-2 bg-purple-50 rounded-md mr-3" data-id="element-3864">
            <ShoppingBagIcon size={18} className="text-purple-600" data-id="element-3865" />
          </div>
          <div data-id="element-3866">
            <p className="text-sm text-gray-500" data-id="element-3867">Orders</p>
            <p className="text-lg font-medium" data-id="element-3868">{shop?.ordersCount}</p>
          </div>
        </div>
      </div>
    </div>;
  // Render tabs
  const renderTabs = () => <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3869">
      <div className="flex border-b border-gray-200" data-id="element-3870">
        <button onClick={() => setActiveTab('dashboard')} className={`pb-4 px-1 mr-8 text-sm font-medium ${activeTab === 'dashboard' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3871">
          Dashboard
        </button>
        <button onClick={() => setActiveTab('products')} className={`pb-4 px-1 mr-8 text-sm font-medium ${activeTab === 'products' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3872">
          Products
        </button>
        <button onClick={() => setActiveTab('categories')} className={`pb-4 px-1 mr-8 text-sm font-medium ${activeTab === 'categories' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3873">
          Categories
        </button>
        <button onClick={() => setActiveTab('settings')} className={`pb-4 px-1 text-sm font-medium ${activeTab === 'settings' ? 'text-blue-600 border-b-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-3874">
          Shop Settings
        </button>
      </div>
    </div>;
  // Add a new renderDashboardTab function
  const renderDashboardTab = () => {
    if (!shop) return null;
    return <ShopDashboard shop={shop} data-id="element-3875" />;
  };
  // Render products tab
  const renderProductsTab = () => <div className="space-y-4" data-id="element-3876">
      {/* Products toolbar */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-3877">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center space-y-4 md:space-y-0" data-id="element-3878">
          <div className="flex-1 w-full md:w-auto" data-id="element-3879">
            <div className="relative" data-id="element-3880">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-3881">
                <SearchIcon size={16} className="text-gray-400" data-id="element-3882" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" placeholder="Search products by name, description or SKU" value={searchTerm} onChange={e => setSearchTerm(e.target.value)} data-id="element-3883" />
            </div>
          </div>
          <div className="flex space-x-2 w-full md:w-auto" data-id="element-3884">
            <div className="relative flex-grow md:flex-grow-0" data-id="element-3885">
              <select className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500" value={selectedCategoryId} onChange={e => setSelectedCategoryId(e.target.value)} data-id="element-3886">
                <option value="all" data-id="element-3887">All Categories</option>
                {categories.map(category => <option key={category.id} value={category.id} data-id="element-3888">
                    {category.name}
                  </option>)}
              </select>
              <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none" data-id="element-3889">
                <FilterIcon size={16} className="text-gray-400" data-id="element-3890" />
              </div>
            </div>
            <div className="flex space-x-1" data-id="element-3891">
              <button onClick={() => setViewMode('grid')} className={`p-2 rounded ${viewMode === 'grid' ? 'bg-gray-200' : 'hover:bg-gray-100'}`} data-id="element-3892">
                <LayoutGridIcon size={18} className="text-gray-700" data-id="element-3893" />
              </button>
              <button onClick={() => setViewMode('list')} className={`p-2 rounded ${viewMode === 'list' ? 'bg-gray-200' : 'hover:bg-gray-100'}`} data-id="element-3894">
                <ListIcon size={18} className="text-gray-700" data-id="element-3895" />
              </button>
            </div>
            <button className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center" data-id="element-3896">
              <PlusIcon size={16} className="mr-1" data-id="element-3897" />
              Add Product
            </button>
          </div>
        </div>
      </div>
      {/* Products list */}
      {viewMode === 'grid' ? <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4" data-id="element-3898">
          {filteredProducts.map(product => <div key={product.id} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-3899">
              <div className="aspect-w-1 aspect-h-1 w-full overflow-hidden bg-gray-200" data-id="element-3900">
                <img src={product.imageUrl} alt={product.name} className="w-full h-48 object-cover" data-id="element-3901" />
                {!product.isActive && <div className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center" data-id="element-3902">
                    <span className="px-2 py-1 bg-red-600 text-white text-xs font-medium rounded" data-id="element-3903">
                      Inactive
                    </span>
                  </div>}
              </div>
              <div className="p-4" data-id="element-3904">
                <h3 className="text-sm font-medium text-gray-900 truncate" data-id="element-3905">
                  {product.name}
                </h3>
                <p className="text-xs text-gray-500 mt-1 truncate" data-id="element-3906">
                  {product.description}
                </p>
                <div className="mt-2 flex justify-between items-center" data-id="element-3907">
                  <div data-id="element-3908">
                    {product.discountedPrice ? <div className="flex items-center" data-id="element-3909">
                        <span className="text-sm font-medium text-gray-900" data-id="element-3910">
                          ${product.discountedPrice.toFixed(2)}
                        </span>
                        <span className="ml-2 text-xs text-gray-500 line-through" data-id="element-3911">
                          ${product.price.toFixed(2)}
                        </span>
                      </div> : <span className="text-sm font-medium text-gray-900" data-id="element-3912">
                        ${product.price.toFixed(2)}
                      </span>}
                  </div>
                  <span className="text-xs text-gray-500" data-id="element-3913">
                    Stock: {product.stockQuantity}
                  </span>
                </div>
                <div className="mt-3 flex justify-between items-center" data-id="element-3914">
                  <span className="text-xs text-gray-500" data-id="element-3915">
                    SKU: {product.sku}
                  </span>
                  <div className="flex space-x-1" data-id="element-3916">
                    <button onClick={() => toggleProductStatus(product.id)} className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-id="element-3917">
                      {product.isActive ? <EyeIcon size={16} data-id="element-3918" /> : <EyeIcon size={16} data-id="element-3919" />}
                    </button>
                    <button className="p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded" data-id="element-3920">
                      <EditIcon size={16} data-id="element-3921" />
                    </button>
                    <button className="p-1 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded" data-id="element-3922">
                      <TrashIcon size={16} data-id="element-3923" />
                    </button>
                  </div>
                </div>
              </div>
            </div>)}
          {/* Add product card */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 border-dashed flex items-center justify-center h-64" data-id="element-3924">
            <button className="flex flex-col items-center text-gray-500 hover:text-blue-600 p-4" data-id="element-3925">
              <PlusIcon size={24} className="mb-2" data-id="element-3926" />
              <span className="text-sm font-medium" data-id="element-3927">Add Product</span>
            </button>
          </div>
        </div> : <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-3928">
          <table className="min-w-full divide-y divide-gray-200" data-id="element-3929">
            <thead className="bg-gray-50" data-id="element-3930">
              <tr data-id="element-3931">
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3932">
                  Product
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3933">
                  Category
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3934">
                  Price
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3935">
                  Stock
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3936">
                  SKU
                </th>
                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3937">
                  Status
                </th>
                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3938">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200" data-id="element-3939">
              {filteredProducts.map(product => {
            const category = categories.find(c => c.id === product.categoryId);
            return <tr key={product.id} className="hover:bg-gray-50" data-id="element-3940">
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3941">
                      <div className="flex items-center" data-id="element-3942">
                        <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded bg-gray-100" data-id="element-3943">
                          <img src={product.imageUrl} alt={product.name} className="h-10 w-10 object-cover" data-id="element-3944" />
                        </div>
                        <div className="ml-4" data-id="element-3945">
                          <div className="text-sm font-medium text-gray-900" data-id="element-3946">
                            {product.name}
                          </div>
                          <div className="text-sm text-gray-500 truncate max-w-xs" data-id="element-3947">
                            {product.description}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3948">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800" data-id="element-3949">
                        {category?.name || 'Uncategorized'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3950">
                      {product.discountedPrice ? <div data-id="element-3951">
                          <span className="text-sm font-medium text-gray-900" data-id="element-3952">
                            ${product.discountedPrice.toFixed(2)}
                          </span>
                          <span className="ml-2 text-xs text-gray-500 line-through" data-id="element-3953">
                            ${product.price.toFixed(2)}
                          </span>
                        </div> : <span className="text-sm font-medium text-gray-900" data-id="element-3954">
                          ${product.price.toFixed(2)}
                        </span>}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3955">
                      <span className={`text-sm ${product.stockQuantity < 10 ? 'text-red-600 font-medium' : 'text-gray-900'}`} data-id="element-3956">
                        {product.stockQuantity}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500" data-id="element-3957">
                      {product.sku}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap" data-id="element-3958">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${product.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`} data-id="element-3959">
                        {product.isActive ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-3960">
                      <div className="flex justify-end space-x-2" data-id="element-3961">
                        <button onClick={() => toggleProductStatus(product.id)} className="text-gray-500 hover:text-blue-600" data-id="element-3962">
                          {product.isActive ? <EyeIcon size={16} data-id="element-3963" /> : <EyeIcon size={16} data-id="element-3964" />}
                        </button>
                        <button className="text-gray-500 hover:text-blue-600" data-id="element-3965">
                          <EditIcon size={16} data-id="element-3966" />
                        </button>
                        <button className="text-gray-500 hover:text-red-600" data-id="element-3967">
                          <TrashIcon size={16} data-id="element-3968" />
                        </button>
                      </div>
                    </td>
                  </tr>;
          })}
            </tbody>
          </table>
        </div>}
      {/* Empty state */}
      {filteredProducts.length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-3969">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-3970">
            <PackageIcon size={24} className="text-blue-600" data-id="element-3971" />
          </div>
          <h3 className="text-lg font-medium text-gray-900" data-id="element-3972">
            No products found
          </h3>
          <p className="text-gray-500 mt-2 mb-6" data-id="element-3973">
            {searchTerm || selectedCategoryId !== 'all' ? 'Try adjusting your search or filter criteria' : 'Get started by adding your first product'}
          </p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors" data-id="element-3974">
            <PlusIcon size={16} className="mr-2" data-id="element-3975" />
            Add Product
          </button>
        </div>}
    </div>;
  // Render categories tab
  const renderCategoriesTab = () => <div className="space-y-4" data-id="element-3976">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 flex justify-between items-center" data-id="element-3977">
        <h3 className="text-lg font-medium text-gray-900" data-id="element-3978">
          Product Categories
        </h3>
        <button className="px-3 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium flex items-center" data-id="element-3979">
          <PlusIcon size={16} className="mr-1" data-id="element-3980" />
          Add Category
        </button>
      </div>
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-3981">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-3982">
          <thead className="bg-gray-50" data-id="element-3983">
            <tr data-id="element-3984">
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3985">
                Category
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3986">
                Description
              </th>
              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3987">
                Products
              </th>
              <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-3988">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-3989">
            {categories.map(category => <tr key={category.id} className="hover:bg-gray-50" data-id="element-3990">
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3991">
                  <div className="flex items-center" data-id="element-3992">
                    <div className="flex-shrink-0 h-10 w-10 bg-blue-100 rounded-full flex items-center justify-center" data-id="element-3993">
                      <TagIcon size={16} className="text-blue-600" data-id="element-3994" />
                    </div>
                    <div className="ml-4" data-id="element-3995">
                      <div className="text-sm font-medium text-gray-900" data-id="element-3996">
                        {category.name}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3997">
                  <div className="text-sm text-gray-500" data-id="element-3998">
                    {category.description}
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap" data-id="element-3999">
                  <div className="text-sm text-gray-900" data-id="element-4000">
                    {category.productCount} products
                  </div>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium" data-id="element-4001">
                  <div className="flex justify-end space-x-2" data-id="element-4002">
                    <button className="text-gray-500 hover:text-blue-600" data-id="element-4003">
                      <EditIcon size={16} data-id="element-4004" />
                    </button>
                    <button className="text-gray-500 hover:text-red-600" data-id="element-4005">
                      <TrashIcon size={16} data-id="element-4006" />
                    </button>
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
      {/* Empty state */}
      {categories.length === 0 && <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center" data-id="element-4007">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4008">
            <TagIcon size={24} className="text-blue-600" data-id="element-4009" />
          </div>
          <h3 className="text-lg font-medium text-gray-900" data-id="element-4010">
            No categories found
          </h3>
          <p className="text-gray-500 mt-2 mb-6" data-id="element-4011">
            Organize your products by creating categories
          </p>
          <button className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md inline-flex items-center transition-colors" data-id="element-4012">
            <PlusIcon size={16} className="mr-2" data-id="element-4013" />
            Add Category
          </button>
        </div>}
    </div>;
  // Render settings tab
  const renderSettingsTab = () => <div className="space-y-4" data-id="element-4014">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4" data-id="element-4015">
        <p className="text-center text-gray-600" data-id="element-4016">
          Shop-specific settings are available in the main Settings page under
          the "Shop Settings" tab.
        </p>
        <div className="flex justify-center mt-4" data-id="element-4017">
          <button onClick={() => window.location.href = '/settings?tab=shop-settings'} className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-md text-sm font-medium" data-id="element-4018">
            Go to Shop Settings
          </button>
        </div>
      </div>
    </div>;
  // Update the renderActiveTabContent function to include the dashboard tab
  const renderActiveTabContent = () => {
    switch (activeTab) {
      case 'dashboard':
        return renderDashboardTab();
      case 'products':
        return renderProductsTab();
      case 'categories':
        return renderCategoriesTab();
      case 'settings':
        return renderSettingsTab();
      default:
        return renderDashboardTab();
    }
  };
  return <div className="space-y-4" data-id="element-4019">
      {/* Back button and page title */}
      <div className="flex items-center" data-id="element-4020">
        <button onClick={navigateToShops} className="mr-4 p-2 rounded-md hover:bg-gray-100" data-id="element-4021">
          <ChevronLeftIcon size={20} className="text-gray-600" data-id="element-4022" />
        </button>
        <h1 className="text-2xl font-bold text-gray-800 flex items-center" data-id="element-4023">
          <ShoppingBagIcon size={24} className="mr-2 text-blue-600" data-id="element-4024" />
          {isNewShop() ? 'Create New Shop' : shop?.name || 'Shop Details'}
        </h1>
      </div>
      {/* Main content */}
      <div className="space-y-4" data-id="element-4025">
        {isEditing || isNewShop() ? renderShopForm() : <>
            {renderShopHeader()}
            {renderTabs()}
            {renderActiveTabContent()}
          </>}
      </div>
    </div>;
}